/**
 * Renders a dine-in bill as a receipt image (PNG) and saves it — the standard
 * restaurant receipt layout: business header, bill no / date / table, an
 * Item · Qty · Rate · Amount table, subtotal, taxes and charges, grand total.
 *
 * Drawn on a canvas rather than as a PDF on purpose: no extra dependency, the
 * ₹ sign renders with the system font (PDF libraries' built-in fonts lack
 * it), and an image is what diners actually share on WhatsApp. Same approach
 * as the table-QR download (dineInQrDownload.ts).
 */
import type { Order, OrderBillLine } from "../../interfaces/OrderObject";
import { formatInr } from "./MoneyHelper";
import { isNativeWebView } from "./platform";

export interface BillImageOptions {
  businessName: string;
  businessAddress?: string | null;
}

const WIDTH = 720;
const PAD = 40;
const SCALE = 2; // crisp on high-DPI screens and when zoomed
const FONT = "'Segoe UI', Roboto, 'Noto Sans', Arial, sans-serif";

// Column right edges (x) for Qty, Rate and Amount; Item takes the rest.
const COL_AMOUNT = WIDTH - PAD;
const COL_RATE = COL_AMOUNT - 130;
const COL_QTY = COL_RATE - 120;
const ITEM_MAX = COL_QTY - 60 - PAD;

const INK = "#0f172a";
const MUTED = "#64748b";
const RULE = "#cbd5e1";

function money(value: number | undefined | null): string {
  return value == null ? "—" : formatInr(value);
}

function billNumber(order: Order): string {
  return order.orderId.replace(/-/g, "").slice(0, 8).toUpperCase();
}

function billDate(iso: string): string {
  const date = iso ? new Date(iso) : new Date();
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function lineName(line: OrderBillLine): string {
  return line.variantLabel ? `${line.productName} (${line.variantLabel})` : line.productName;
}

/** Splits text into lines that fit maxWidth at the context's current font. */
function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !current) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines.length ? lines : [""];
}

/**
 * Lays the receipt out twice: once to measure its height, once to draw it.
 * `draw` is false on the measuring pass, so every step only advances `y`.
 */
function layout(ctx: CanvasRenderingContext2D, order: Order, opts: BillImageOptions, draw: boolean): number {
  const bill = order.bill;
  let y = PAD;

  const text = (
    value: string,
    x: number,
    font: string,
    color = INK,
    align: CanvasTextAlign = "left"
  ) => {
    if (!draw) return;
    ctx.font = font;
    ctx.fillStyle = color;
    ctx.textAlign = align;
    ctx.fillText(value, x, y);
  };
  const rule = (dashed = false) => {
    if (draw) {
      ctx.strokeStyle = RULE;
      ctx.lineWidth = 1;
      ctx.setLineDash(dashed ? [6, 5] : []);
      ctx.beginPath();
      ctx.moveTo(PAD, y);
      ctx.lineTo(WIDTH - PAD, y);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  };
  const row = (left: string, right: string, font: string, color = INK) => {
    text(left, PAD, font, color);
    text(right, WIDTH - PAD, font, color, "right");
  };

  // Header: business name (wrapped), address, then the document title.
  ctx.font = `700 30px ${FONT}`;
  for (const part of wrap(ctx, opts.businessName || "Restaurant", WIDTH - PAD * 2)) {
    y += 32;
    text(part, WIDTH / 2, `700 30px ${FONT}`, INK, "center");
  }
  if (opts.businessAddress) {
    ctx.font = `400 17px ${FONT}`;
    for (const part of wrap(ctx, opts.businessAddress, WIDTH - PAD * 2)) {
      y += 24;
      text(part, WIDTH / 2, `400 17px ${FONT}`, MUTED, "center");
    }
  }
  y += 40;
  text("BILL", WIDTH / 2, `700 20px ${FONT}`, INK, "center");
  y += 22;
  rule(true);

  // Bill meta.
  y += 30;
  row(`Bill No: ${billNumber(order)}`, billDate(order.createdAt), `500 17px ${FONT}`);
  if (bill?.tableNumber) {
    y += 26;
    text(`Table: ${bill.tableNumber}`, PAD, `500 17px ${FONT}`);
  }
  y += 18;
  rule(true);

  // Column headers.
  y += 28;
  const head = `700 16px ${FONT}`;
  text("Item", PAD, head, MUTED);
  text("Qty", COL_QTY, head, MUTED, "right");
  text("Rate", COL_RATE, head, MUTED, "right");
  text("Amount", COL_AMOUNT, head, MUTED, "right");
  y += 14;
  rule();

  // Lines — the item name wraps; numbers sit on its first line.
  const body = `400 18px ${FONT}`;
  for (const line of bill?.lines ?? []) {
    ctx.font = body;
    const nameLines = wrap(ctx, lineName(line), ITEM_MAX);
    y += 30;
    text(nameLines[0], PAD, body);
    text(String(line.quantity), COL_QTY, body, INK, "right");
    const rate = line.unitPrice ?? (line.quantity ? line.lineTotal / line.quantity : null);
    text(money(rate), COL_RATE, body, INK, "right");
    text(money(line.lineTotal), COL_AMOUNT, body, INK, "right");
    for (const extra of nameLines.slice(1)) {
      y += 24;
      text(extra, PAD, body);
    }
  }
  y += 18;
  rule();

  // Subtotal, then each tax/charge/round-off as stored on the bill.
  const charges = bill?.charges ?? [];
  const subtotal =
    bill?.subtotal ?? (bill?.lines ?? []).reduce((sum, line) => sum + (line.lineTotal ?? 0), 0);
  const small = `400 17px ${FONT}`;
  y += 30;
  row("Subtotal", money(subtotal), small);
  for (const charge of charges) {
    y += 26;
    row(charge.label, money(charge.amount), small, MUTED);
  }
  y += 20;
  rule();

  // Grand total.
  y += 38;
  row("Grand Total", money(order.totalPrice), `800 24px ${FONT}`);
  y += 20;
  rule(true);

  // Footer.
  y += 36;
  text("Thank you! Visit again.", WIDTH / 2, `500 17px ${FONT}`, MUTED, "center");
  y += 26;
  text("Billed via NineFinds", WIDTH / 2, `400 14px ${FONT}`, MUTED, "center");

  return y + PAD;
}

/** Draws the receipt and returns it as a PNG blob. */
export async function renderBillImage(order: Order, opts: BillImageOptions): Promise<Blob> {
  const measure = document.createElement("canvas").getContext("2d");
  if (!measure) throw new Error("Couldn't create the bill image.");
  const height = layout(measure, order, opts, false);

  const canvas = document.createElement("canvas");
  canvas.width = WIDTH * SCALE;
  canvas.height = height * SCALE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Couldn't create the bill image.");
  ctx.scale(SCALE, SCALE);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, WIDTH, height);
  ctx.textBaseline = "alphabetic";
  layout(ctx, order, opts, true);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("Couldn't create the bill image.");
  return blob;
}

export function billFileName(order: Order, businessName: string): string {
  const slug = businessName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "bill";
  return `${slug}-bill-${billNumber(order)}.png`;
}

/**
 * Saves the receipt. In a browser it downloads the PNG. Inside the native app
 * there is no download manager for a generated file (the native download
 * bridge only takes http URLs), so it opens the share sheet instead, where
 * the diner can save it or send it on. Throws a user-facing message when
 * neither is possible.
 */
export async function downloadBillImage(order: Order, opts: BillImageOptions): Promise<void> {
  const blob = await renderBillImage(order, opts);
  const filename = billFileName(order, opts.businessName);

  if (isNativeWebView()) {
    const file = new File([blob], filename, { type: "image/png" });
    if (typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: "Bill" });
      return;
    }
    throw new Error("Saving the bill isn't supported in this app version yet — please take a screenshot.");
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick: Safari cancels the download if the URL disappears in the same frame.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export default { renderBillImage, downloadBillImage, billFileName };
